import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Calendar, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import OptimizedImage from '@/components/ui/OptimizedImage';
import heroImage from '@/assets/hero-devices.jpg';
import iphoneRepair from '@/assets/iphone-repair.jpg';
import macbookRepair from '@/assets/macbook-repair.jpg';
import ipadRepair from '@/assets/ipad-repair.jpg';
import watchRepair from '@/assets/watch-repair.jpg';

interface HeroSectionProps {
  onBookingOpen: () => void;
}

const heroMedia = [
  { type: 'image' as const, src: heroImage, alt: 'Apple device repair services at iFix Apple Care Patna - iPhone MacBook iPad Apple Watch' },
  { type: 'video' as const, src: 'https://tempfile.aiquickdraw.com/f/22e4f6c1e703d30d9b450fd0911abfa9/c0fb8f10-e686-433a-9782-94bf6a8cb8fd.mp4', alt: 'iPhone screen replacement video at iFix Apple Care service center Patna Bihar' },
  { type: 'image' as const, src: iphoneRepair, alt: 'iPhone repair service in Patna Bihar - screen replacement battery fix' },
  { type: 'video' as const, src: 'https://tempfile.aiquickdraw.com/f/66de07101000956d00113561f51dd816/c3176e0a-d0ae-466c-82b1-bf56da3404c1.mp4', alt: 'MacBook repair video by certified technicians at iFix Apple Care Bihar' },
  { type: 'image' as const, src: macbookRepair, alt: 'MacBook repair service in Patna - logic board keyboard battery replacement' },
  { type: 'image' as const, src: ipadRepair, alt: 'iPad repair service in Bihar - screen replacement charging port fix' },
  { type: 'image' as const, src: watchRepair, alt: 'Apple Watch repair service in Patna - screen battery sensor replacement' },
];

const HeroSection = ({ onBookingOpen }: HeroSectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroMedia.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        if (rect.bottom > 0) {
          setScrollY(window.scrollY);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mouse parallax effect (desktop only)
  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) return;

    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const parallaxOffset = scrollY * 0.3;
  const imageParallaxOffset = scrollY * 0.15;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <section 
      ref={heroRef}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-background"
    >
      {/* Fixed background parallax for mobile */}
      <div 
        className="absolute inset-0 md:hidden"
        style={{
          backgroundImage: `linear-gradient(to bottom, hsl(var(--secondary) / 0.4), hsl(var(--background) / 0.95)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          WebkitBackgroundSize: 'cover',
        }}
      />
      
      {/* Animated parallax background layers - Desktop */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-background to-background hidden md:block"
        style={{ transform: `translateY(${parallaxOffset * 0.2}px)` }}
      />
      
      {/* Floating gradient orbs with parallax - Desktop only */}
      <div 
        className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-gradient-radial from-primary/8 via-primary/3 to-transparent rounded-full blur-3xl animate-parallax-drift hidden md:block"
        style={{ 
          transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * 20 + parallaxOffset * 0.1}px)` 
        }}
      />
      <div 
        className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-gradient-radial from-accent/10 via-transparent to-transparent rounded-full blur-3xl hidden md:block"
        style={{ 
          transform: `translate(${mousePosition.x * -15}px, ${mousePosition.y * -15 + parallaxOffset * 0.15}px)`,
          animationDelay: '-4s'
        }}
      />
      
      {/* Mobile gradient overlay for fixed background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/60 to-background md:hidden" />

      {/* Main Content */}
      <div className="relative z-10 container-apple text-center px-6">
        {/* Badge */}
        <div 
          className={`inline-flex items-center gap-2 px-5 py-2.5 bg-secondary rounded-full text-sm text-muted-foreground mt-20 mb-8 border border-border/50 shadow-lg shadow-primary/10 dark:shadow-primary/5 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
          </span>
          Oldest & Most Trusted Apple Service Centre in Patna, Bihar
        </div>

        {/* Headline - H1 with keywords */}
        <h1 className="mb-6">
          <span 
            className={`block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-semibold text-foreground tracking-tight transition-all duration-1000 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Apple Service Center Patna
          </span>
          <span 
            className={`block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-primary tracking-tight mt-2 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            iPhone, MacBook, iPad & Watch Repair
          </span>
        </h1>

        {/* Subheadline with internal links */}
        <p 
          className={`max-w-2xl mx-auto text-lg md:text-xl text-muted-foreground mb-10 leading-relaxed transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Bihar's premier Apple service center in Patna offering expert{' '}
          <Link to="/iphone-service" className="text-primary hover:underline">iPhone repair</Link>,{' '}
          <Link to="/macbook-service" className="text-primary hover:underline">MacBook service center</Link>,{' '}
          <Link to="/ipad-service" className="text-primary hover:underline">iPad repair services</Link> &{' '}
          <Link to="/watch-service" className="text-primary hover:underline">Apple Watch repair</Link>.
          iPhone screen replacement, iPhone battery replacement & more with genuine parts and same-day service.
        </p>

        {/* CTA Buttons */}
        <div 
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <Button 
            variant="apple-blue" 
            size="lg"
            onClick={onBookingOpen}
            className="min-w-[200px]"
          >
            <Calendar className="w-5 h-5" />
            Book Repair
          </Button>
          <Button variant="whatsapp" size="lg" asChild className="min-w-[200px]">
            <a
              href="https://wa.me/919798653591?text=Hi,%20I%20need%20help%20with%20my%20Apple%20device"
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp
            </a>
          </Button>
          <Button variant="call" size="lg" asChild className="min-w-[200px]">
            <a href="tel:9798653591">
              <Phone className="w-5 h-5" />
              Call Now
            </a>
          </Button>
        </div>

        {/* Hero Image with parallax */}
        <div 
          className={`relative max-w-4xl mx-auto transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
          }`}
          style={{ 
            transform: `translateY(${imageParallaxOffset}px) scale(${isVisible ? 1 : 0.95})`,
          }}
        >
          <div 
            className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-accent/10 to-primary/20 rounded-3xl blur-2xl opacity-50"
            style={{ 
              transform: `translate(${mousePosition.x * 10}px, ${mousePosition.y * 10}px)` 
            }}
          />
          <div className="relative rounded-2xl shadow-2xl ring-1 ring-border/10 overflow-hidden aspect-video">
            {heroMedia.map((media, index) => (
              media.type === 'video' ? (
                <video
                  key={media.alt}
                  src={media.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label={media.alt}
                  title={media.alt}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[3000ms] ease-in-out ${
                    index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <track kind="descriptions" label={media.alt} />
                </video>
              ) : (
                <div
                  key={media.alt}
                  style={{
                    animationPlayState: index === currentImageIndex ? 'running' : 'paused',
                  }}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-[3000ms] ease-in-out ${
                    index === currentImageIndex 
                      ? 'opacity-100 animate-ken-burns' 
                      : 'opacity-0'
                  }`}
                >
                  <OptimizedImage
                    src={media.src}
                    alt={media.alt}
                    priority={index === 0}
                    className="w-full h-full object-cover"
                  />
                </div>
              )
            ))}
            {/* Vignette overlay */}
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.4)_100%)]" />
            {/* Film grain overlay */}
            <div 
              className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              }}
            />
          </div>
          {/* Image indicators */}
          <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {heroMedia.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentImageIndex 
                    ? 'bg-primary w-6' 
                    : 'bg-muted-foreground/40 hover:bg-muted-foreground/60'
                }`}
                aria-label={`View media ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Stats Row */}
        <div 
          className={`flex flex-wrap items-center justify-center gap-8 md:gap-16 mt-16 transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {[
            { value: '28,000+', label: 'Devices Repaired' },
            { value: '10+', label: 'Years Experience' },
            { value: 'Same Day', label: 'Repair' },
            { value: 'Warranty', label: 'On All Repairs' },
          ].map((stat) => (
          <div key={stat.label} className="text-center">
              <div className="text-3xl md:text-4xl font-semibold text-foreground">
                {stat.value}
              </div>
              <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div 
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-1000 delay-1000 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <span className="text-xs uppercase tracking-widest">Explore</span>
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;